import { XCircleIcon } from '@heroicons/react/24/solid';
import React, { useMemo, useState } from 'react';

import { DashedTag, SearchBar } from '@/components/chariot-ui';
import { Dropdown } from '@/components/Dropdown';
import { FormGroup } from '@/components/form/FormGroup';
import { Capability } from '@/hooks/useCapabilities';
import { useMy } from '@/hooks/useMy';
import {
  buildTargetFilters,
  getCapabilityMatchConfig,
  getMatchDescription,
} from '@/sections/attacks/capabilities/utils/target-match-conditions';
import { parseKeys } from '@/sections/SearchByType';
import { AssetStatus, MyResource, NodeLabel } from '@/types';
import { capitalize } from '@/utils/lodash.util';

/**
 * Map target type to the correct NodeLabel for queries.
 * Handles special cases where the label differs from simple capitalization.
 */
function getTargetNodeLabel(targetType?: string): NodeLabel {
  if (!targetType) return 'Asset';

  switch (targetType) {
    case 'addomain':
      return 'ADDomain';
    case 'webapplication':
      return 'WebApplication';
    default:
      return capitalize(targetType) as NodeLabel;
  }
}

/**
 * Props for the TargetSelector component.
 *
 * Used in CapabilityDrawer to select targets (assets, ports, repositories, etc.)
 * for capability execution. The component filters available targets based on
 * capability-specific match conditions.
 */
interface TargetSelectorProps {
  /**
   * Currently selected target keys.
   *
   * Target keys follow the format: `#{type}#{identifier}`
   *
   * @example
   * // Port key
   * '#port#192.168.1.1:80#tcp'
   *
   * @example
   * // Asset key
   * '#asset#example.com'
   */
  selectedTargets: string[];

  /**
   * Callback invoked when the selection changes.
   *
   * @param targets - Updated array of selected target keys
   */
  onTargetsChange: (targets: string[]) => void;

  /**
   * The capability being configured.
   *
   * Determines:
   * - Target type (port, asset, repository, etc.)
   * - Match conditions for filtering available targets
   * - Display labels and empty state messages
   *
   * @optional May be undefined during initial render
   */
  capability?: Capability;

  /**
   * Function that returns a human-readable label for the target type.
   *
   * Used for:
   * - Form label: "Select {label}"
   * - Search placeholder: "Search {label}..."
   * - Selection count: "X {label} selected"
   *
   * @returns Pluralized, capitalized label (e.g., "Assets", "Ports", "Repositories")
   *
   * @example
   * getTargetLabel() // Returns "Ports" for port-based capability
   * getTargetLabel() // Returns "AD Domains" for addomain capability
   */
  getTargetLabel: () => string;
}

interface TargetItem {
  key: string;
  group?: string;
  identifier?: string;
  source?: string;
  name?: string;
  value?: string;
  domain?: string;
  dns?: string;
  url?: string;
  service?: string;
  port?: number;
}

/**
 * Result of resolving target display data.
 * - text: Plain text representation (always present)
 * - jsx: Optional JSX representation (for complex rendering)
 */
interface DisplayResult {
  text: string;
  jsx?: React.ReactNode;
}

/**
 * Resolve display data for a target item.
 * Single source of truth for all target display logic.
 *
 * @param item - The target item to resolve display for
 * @param type - The target type (asset, attribute, port, etc.)
 * @returns DisplayResult with text and optional jsx
 */
function resolveTargetDisplay(item: TargetItem, type: string): DisplayResult {
  switch (type) {
    case 'asset':
      return { text: item.group || item.identifier || item.key };

    case 'attribute':
      if (item.source) {
        const parsedAsset = parseKeys.assetKey(item.source);
        const text = `${parsedAsset.identifier} - ${item.name}:${item.value}`;
        const jsx = (
          <span>
            {parsedAsset.identifier} - {item.name}:{item.value}
          </span>
        );
        return { text, jsx };
      }
      if (item.name && item.value) {
        return { text: `${item.name}:${item.value}` };
      }
      return { text: item.name || item.key };

    case 'addomain':
      return {
        text: (
          item.domain ||
          item.group ||
          item.name ||
          item.key
        ).toUpperCase(),
      };

    case 'domain':
      return { text: item.dns || item.name || item.key };

    case 'seed':
      return { text: item.identifier || item.key };

    case 'preseed':
      return { text: item.name || item.dns || item.key };

    case 'webpage':
      return { text: item.url || item.name || item.key };

    case 'port': {
      const portData = parseKeys.portKey(item.key);
      const assetData = parseKeys.assetKey(portData.assetKey);
      const service = item.service ? ` (${item.service})` : '';
      const text = `${assetData.identifier} - ${portData.protocol.toUpperCase()}/${portData.port}${service}`;
      const jsx = (
        <span>
          {assetData.identifier} - {portData.protocol.toUpperCase()}/
          {portData.port}
          {service}
        </span>
      );
      return { text, jsx };
    }

    default:
      return { text: item.name || item.dns || item.key };
  }
}

/**
 * TargetSelector - Component for selecting targets (assets, attributes, etc.) for capability execution
 *
 * Provides a searchable dropdown to select targets based on the capability's target type.
 * Supports multiple selection with visual feedback for selected items.
 */
export function TargetSelector({
  selectedTargets,
  onTargetsChange,
  capability,
  getTargetLabel,
}: TargetSelectorProps) {
  const [searchValue, setSearchValue] = useState('');

  const targetType = capability?.target?.[0]?.toLowerCase();

  // Get capability-specific match configuration for filtering
  // Applies to all target types: port, asset, repository, etc.
  const matchConfig = getCapabilityMatchConfig(capability?.name);
  const capabilityFilters = useMemo(() => {
    if (!matchConfig) return [];
    // Only apply filters if the target type matches the config
    if (matchConfig.target !== targetType) return [];
    return buildTargetFilters(matchConfig);
  }, [matchConfig, targetType]);

  // Use useMy with GraphQuery for proper filtering
  const { data: targets, status } = useMy(
    {
      resource:
        (targetType === 'domain'
          ? 'asset'
          : (targetType as keyof MyResource)) || 'asset',
      query: {
        node: {
          labels: [getTargetNodeLabel(targetType)],
          filters: [
            // Exclude deleted and frozen
            {
              field: 'status',
              operator: '=',
              value: AssetStatus.Frozen,
              not: true,
            },
            {
              field: 'status',
              operator: '=',
              value: AssetStatus.Deleted,
              not: true,
            },
            // For domain targets, filter to domain/TLD asset classes only
            ...(targetType === 'domain'
              ? [
                  {
                    field: '',
                    operator: 'OR' as const,
                    value: [
                      {
                        field: 'class',
                        operator: '=' as const,
                        value: 'domain',
                      },
                      { field: 'class', operator: '=' as const, value: 'tld' },
                    ],
                  },
                ]
              : []),
            // Apply capability-specific target filters (e.g., specific ports for java-rmi-scanner)
            ...capabilityFilters,
            ...(searchValue
              ? targetType === 'port'
                ? // For ports, search by key OR service OR port number
                  [
                    {
                      field: '',
                      operator: 'OR' as const,
                      value: [
                        {
                          field: 'key',
                          operator: 'CONTAINS' as const,
                          value: searchValue,
                        },
                        {
                          field: 'service',
                          operator: 'CONTAINS' as const,
                          value: searchValue,
                        },
                        // If search value is a number, also search by port number
                        ...(isNaN(Number(searchValue))
                          ? []
                          : [
                              {
                                field: 'port',
                                operator: '=' as const,
                                value: Number(searchValue),
                              },
                            ]),
                      ],
                    },
                  ]
                : // For other types, use default key search
                  [
                    {
                      field: 'key',
                      operator: 'CONTAINS' as const,
                      value: searchValue,
                    },
                  ]
              : []),
          ],
        },
        limit: 10,
      },
    },
    { enabled: !!targetType }
  );

  // Get display label (JSX when available, fallback to text)
  const getDisplayLabel = (item: TargetItem, type: string): React.ReactNode => {
    const result = resolveTargetDisplay(item, type);
    return result.jsx ?? result.text;
  };

  // Create options with target-specific filtering and display
  const options = useMemo((): Array<{
    value: string;
    label: React.ReactNode;
    id: string;
  }> => {
    if (!targets?.length) return [];

    return (targets as TargetItem[])
      .filter((item: TargetItem) => {
        // Filter by target type using key prefix
        const expectedPrefix = `#${targetType === 'domain' ? 'asset' : targetType}#`;
        if (!item.key.startsWith(expectedPrefix)) return false;

        return true;
      })
      .slice(0, 50)
      .map((item: TargetItem) => ({
        value: item.key,
        label: getDisplayLabel(item, targetType || 'asset'),
        id: item.key,
      }));
  }, [targets, targetType]);

  const removeTarget = (key: string) => {
    onTargetsChange(selectedTargets.filter(t => t !== key));
  };

  // Get empty state label with capability-specific messaging
  const getEmptyStateLabel = (): string => {
    if (status === 'pending') return 'Loading...';

    // If capability has specific requirements, show helpful message
    if (capability?.name) {
      const matchDescription = getMatchDescription(capability.name);
      if (matchDescription) {
        return `No matching targets. This capability requires ${matchDescription}.`;
      }
    }

    return 'No results found';
  };

  // Get a string version of the display name for use in DashedTag labels
  const getDisplayNameString = (key: string): string => {
    const item = (targets as TargetItem[] | undefined)?.find(
      (t: TargetItem) => t.key === key
    );
    if (!item) return key;
    return resolveTargetDisplay(item, targetType || 'asset').text;
  };

  return (
    <FormGroup label={`Select ${getTargetLabel()}`} name="target-selector">
      <div className="space-y-3">
        {/* Search Input */}
        <Dropdown
          focusType="click"
          menu={{
            items: options.map(option => ({
              label: option.label,
              value: option.value,
            })),
            value: selectedTargets,
            onSelect: onTargetsChange,
            multiSelect: true,
            emptyState: {
              hide: options.length > 0,
              label: getEmptyStateLabel(),
            },
            className: 'max-h-[300px]',
          }}
          asChild
        >
          <div>
            <SearchBar
              size="sm"
              className="h-fit w-full"
              placeholder={`Search ${getTargetLabel()}...`}
              value={searchValue}
              onChange={setSearchValue}
            />
          </div>
        </Dropdown>

        {/* Selected Targets as Tags */}
        {selectedTargets.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedTargets.map(target => (
              <div key={target} className="flex items-center gap-1">
                <DashedTag
                  icon={<XCircleIcon className="size-5" />}
                  className="text-chariot-text-secondary py-1"
                  size="sm"
                  onClick={() => removeTarget(target)}
                  label={getDisplayNameString(target)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </FormGroup>
  );
}
